import {
  useBrokersServiceBrokersBrokersCreate,
  useBrokersServiceBrokersBrokersInviteAdminCreate,
  useBrokersServiceBrokersBrokersListKey,
  useBrokersServiceBrokersBrokersMediaCreate,
  useBrokersServiceBrokersBrokersPartialUpdate,
  useBrokersServiceBrokersBrokersRetrieve,
  useBrokersServiceBrokersBrokersRetrieveKey
} from '@insurance/api/queries'
import { Broker, BrokerInvite } from '@insurance/api/requests'
import { useValidationsField } from '@insurance/hooks'
import { BrokerStatus } from '@insurance/types'
import { PhoneInput } from '@shared/components'
import { useInvalidateQueries } from '@shared/hooks'
import {
  formatDateToISOFormat,
  replaceEmptyWithNull,
  replaceEmptyWithUndefined,
  showApiErrors
} from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { Col, Form, FormProps, Input, message, Row, UploadFile } from 'antd'
import { UploadChangeParam } from 'antd/es/upload'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { CompaniesContractField } from './CompaniesContractField'

type Props = {
  companyId?: number
  onLoading?: React.Dispatch<React.SetStateAction<boolean>>
  onLoadingModal?: React.Dispatch<React.SetStateAction<boolean>>
  onNextStep?: (id: number) => void
  onAddCompanyId?: (id?: number) => void
} & FormProps

export const CompaniesManagementForm = ({
  companyId,
  onLoading,
  onLoadingModal,
  onNextStep,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidateQueries()
  const { required, fullName, companyName, phoneNumber, personalCode } =
    useValidationsField()
  const queryClient = useQueryClient()

  const { data: company, isLoading: isCompanyLoading } =
    useBrokersServiceBrokersBrokersRetrieve(
      {
        id: Number(companyId)
      },
      [companyId],
      {
        enabled: !!companyId
      }
    )

  useEffect(() => {
    if (company) {
      form.setFieldsValue({
        ...company,
        ...(company.contract_valid_from && {
          contract_valid_from: dayjs(company.contract_valid_from)
        }),
        ...(company.contract_valid_to && {
          contract_valid_to: dayjs(company.contract_valid_to)
        })
      })
    }
  }, [company, form])

  const {
    mutateAsync: createMutation,
    isPending: isCreatePending,
    data: createdData
  } = useBrokersServiceBrokersBrokersCreate({
    onSuccess: () =>
      invalidateQueries([useBrokersServiceBrokersBrokersListKey]),
    onError: (error) => showApiErrors(error, form)
  })

  const id = createdData ? createdData.id : companyId

  const { mutateAsync: updateMutation, isPending: isUpdatePending } =
    useBrokersServiceBrokersBrokersPartialUpdate({
      onError: (error) => showApiErrors(error, form)
    })

  const { mutateAsync: inviteMutation, isPending: isInvitePending } =
    useBrokersServiceBrokersBrokersInviteAdminCreate({
      onError: (error) => showApiErrors(error, form)
    })

  const { mutateAsync: uploadContractMutation, isPending: isUploadPending } =
    useBrokersServiceBrokersBrokersMediaCreate({
      onError: (error) => showApiErrors(error, form)
    })

  const onMutationSuccess = (id: number) => {
    invalidateQueries([useBrokersServiceBrokersBrokersListKey])

    onNextStep?.(id)
  }

  const onFinish = async (
    values: Omit<Broker, 'contract'> &
      BrokerInvite & { contract: UploadChangeParam<UploadFile> }
  ) => {
    const {
      email,
      first_name,
      last_name,
      phone,
      contract,
      identification_number,
      ...restData
    } = values
    const inviteData: BrokerInvite = {
      email,
      first_name,
      last_name,
      phone,
      identification_number
    }

    const hasSomeFieldChanged = form.getFieldsError().some(({ name }) => {
      const key = name[0] as keyof Broker

      if (key === 'contract') return false
      if (Object.keys(inviteData).includes(key)) return false

      if (dayjs.isDayjs(values?.[key]))
        return formatDateToISOFormat(values?.[key]) !== company?.[key]

      return values?.[key] !== company?.[key]
    })

    const brokerData = {
      ...restData,
      contract_valid_from: restData.contract_valid_from
        ? formatDateToISOFormat(restData.contract_valid_from)
        : undefined,
      contract_valid_to: restData.contract_valid_from
        ? formatDateToISOFormat(restData.contract_valid_to)
        : undefined
    } as Broker

    const broker = id
      ? hasSomeFieldChanged
        ? await updateMutation?.({
            id: id as number,
            requestBody: replaceEmptyWithNull(brokerData)
          })
        : await Promise.resolve(company)
      : await createMutation?.({
          requestBody: replaceEmptyWithUndefined(brokerData)
        })

    queryClient.setQueryData(
      [useBrokersServiceBrokersBrokersRetrieveKey, broker?.id],
      broker
    )

    if (contract?.file?.originFileObj)
      await uploadContractMutation({
        formData: {
          id: 0,
          broker: broker?.id as number,
          file: contract?.file?.originFileObj as unknown as string
        }
      })

    if (broker?.status === 'draft')
      await inviteMutation({
        id: broker?.id as number,
        requestBody: replaceEmptyWithUndefined(inviteData)
      })

    message.success(t(companyId ? 'company_updated' : 'company_created'))
    onMutationSuccess(Number(broker?.id))
  }

  const isLoading =
    isCreatePending || isInvitePending || isUpdatePending || isUploadPending

  useEffect(() => {
    onLoading?.(isLoading)
    onLoadingModal?.(isCompanyLoading)

    return () => {
      onLoading?.(false)
      onLoadingModal?.(false)
    }
  }, [isLoading, isCompanyLoading, onLoading, onLoadingModal])

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} {...props}>
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item
            name="name"
            label={t('company_name')}
            rules={[required, companyName]}
          >
            <Input placeholder={t('company_name_placeholder')} />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item name="idno" label={t('idno')} rules={[required]}>
            <Input placeholder={t('idno_placeholder')} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={12}>
          <Form.Item name="iban" label={t('iban_premiums')} rules={[required]}>
            <Input placeholder={t('iban_premiums_placeholder')} />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item name="iban_commission" label={t('iban_commissions')}>
            <Input placeholder={t('iban_commissions_placeholder')} />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            name="address"
            label={t('legal_address')}
            rules={[required]}
          >
            <Input placeholder={t('legal_address_placeholder')} />
          </Form.Item>
        </Col>
      </Row>

      {(!company || company?.status === BrokerStatus.DRAFT) && (
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={t('administrator_last_name')}
              name="last_name"
              rules={[required, fullName]}
            >
              <Input placeholder={t('administrator_last_name')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t('administrator_first_name')}
              name="first_name"
              rules={[required, fullName]}
            >
              <Input placeholder={t('administrator_first_name')} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label={t('identification_number')}
              name="identification_number"
              normalize={(value, prevValue) =>
                String(value).length > 13 ? prevValue : value
              }
              rules={[required, personalCode]}
            >
              <Input placeholder={t('identification_number')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t('phone')}
              name="phone"
              rules={[required, phoneNumber]}
            >
              <PhoneInput />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t('email')}
              name="email"
              rules={[{ type: 'email' }, required]}
            >
              <Input placeholder={t('email')} />
            </Form.Item>
          </Col>
        </Row>
      )}

      {!id && (
        <Row>
          <Col span={24}>
            <CompaniesContractField company={company} />
          </Col>
        </Row>
      )}
    </Form>
  )
}
